import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const Captcha = ({ onVerify, onError }) => {
  const { t } = useLanguage();

  const [captchaText, setCaptchaText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState('');

  // Gerar texto aleatório para o CAPTCHA
  const generateCaptchaText = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Gerar novo CAPTCHA
  const refreshCaptcha = useCallback(() => {
    const newCaptcha = generateCaptchaText();
    setCaptchaText(newCaptcha);
    setUserInput('');
    setIsVerified(false);
    setError('');
    onVerify(false);
  }, [onVerify]);


  // Verificação automática quando o usuário digita 5 caracteres
  const handleInputChange = (e) => {
    const value = e.target.value;
    setUserInput(value);

    // Se o usuário digitou 5 caracteres, verificar automaticamente
    if (value.length === 5) {
      // Pequeno delay para permitir que o usuário termine de digitar
      setTimeout(() => {
        if (value.toLowerCase() === captchaText.toLowerCase()) {
          setIsVerified(true);
          setError('');
          onVerify(true);
        } else {
          setIsVerified(false);
          setError(t('captcha.messages.incorrect'));
          onError('Código incorreto');
          onVerify(false);
        }
      }, 100);
    } else if (value.length < 5) {
      // Reset do estado se o usuário apagar caracteres
      setIsVerified(false);
      setError('');
      onVerify(false);
    }
  };

  // Gerar CAPTCHA inicial
  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        {/* CAPTCHA Visual */}
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div
              className="bg-gray-100 border-2 border-gray-300 rounded-lg px-4 py-3 text-2xl font-bold text-gray-800 tracking-wider select-none"
              style={{
                fontFamily: 'monospace',
                letterSpacing: '0.2em',
                background: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                backgroundSize: '20px 20px',
                backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
              }}
            >
              {captchaText}
            </div>
            <button
              type="button"
              onClick={refreshCaptcha}
              className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs transition-colors duration-200"
              title={t('captcha.refreshTooltip')}
            >
              🔄
            </button>
          </div>
        </div>

        {/* Input do usuário */}
        <div className="flex-1">
          <input
            type="text"
            value={userInput}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200 ${
              error ? 'border-red-300 bg-red-50' :
              isVerified ? 'border-green-300 bg-green-50' :
              'border-gray-300'
            }`}
            placeholder={t('captcha.placeholder')}
            maxLength="5"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Mensagens de erro/sucesso */}
      {error && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {isVerified && (
        <div className="flex items-center space-x-2 text-green-600 text-sm">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>{t('captcha.messages.success')}</span>
        </div>
      )}

      {/* Instruções */}
      <div className="text-xs text-gray-500">
        <p>{t('captcha.instructions')}</p>
      </div>
    </div>
  );
};

export default Captcha;
